import { BankType } from 'apps/user/types/on-boarding.type';
import { AxiosError, AxiosInstance } from 'axios';
import {
  AssignBeneficiaryDto,
  Bank,
  BankList,
  Banks,
  VerifyAccount
} from 'service/dto/bank.dto';
import { BeneficiariesAllocationDto } from 'service/dto/general.dto';
import { errorHandler } from 'utils';

class BankService {
  private baseService: AxiosInstance;

  constructor(baseService: AxiosInstance) {
    this.baseService = baseService;
  }

  createBank = async (bank: BankType) => {
    try {
      const response = await this.baseService.post('/bank/create', bank);
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  verifyBankAccount = async (verifyAccount: VerifyAccount) => {
    try {
      const response = await this.baseService.post(
        '/bank/verify-account',
        verifyAccount
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getBanks = async (): Promise<Banks> => {
    try {
      const response = await this.baseService.get('/bank');
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getBank = async (bankId: number): Promise<Bank> => {
    try {
      const response = await this.baseService.get(`/bank/${bankId}`);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getNigeriaBanks = async (): Promise<BankList[]> => {
    try {
      const response = await this.baseService.get(
        '/bank/list-of-bank-in-nigeria'
      );
      if (response.data) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  deleteBank = async (bankId: number): Promise<void> => {
    try {
      const response = await this.baseService.delete(`/bank/${bankId}`);
      return response.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  verifyBVN = async (bvn: string) => {
    try {
      const response = await this.baseService.get(`/bank/verify-bvn/${bvn}`);
      if (response.data) {
        return response.data;
      }
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  assignBeneficiary = async (assignBeneficiaryDto: AssignBeneficiaryDto) => {
    try {
      const response = await this.baseService.patch(
        '/bank/assign-to-beneficiary',
        assignBeneficiaryDto
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  unAssignBankBeneficiary = async (allocationId?: number): Promise<void> => {
    try {
      const response = await this.baseService.delete(
        `/bank/unassign-beneficiary/${allocationId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  editBulkBankBeneficiary = async (
    beneficiariesAllocationDto?: BeneficiariesAllocationDto
  ): Promise<void> => {
    try {
      const response = await this.baseService.patch(
        `/bank/bulk-edit-beneficiary-assign`,
        beneficiariesAllocationDto
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  removeBankTrustee = async (
    assetId: number,
    trusteeId: number
  ): Promise<void> => {
    try {
      const response = await this.baseService.delete(
        `/bank/unassign-trustee/${assetId}/${trusteeId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  assignBankTrustee = async (
    assetId: number[],
    trusteeId: number,
    note: string
  ): Promise<void> => {
    try {
      const response = await this.baseService.put(`/bank/assign/bulk-trustee`, {
        assetId,
        trusteeId,
        note
      });
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };
}
export default BankService;
